/* Shared sidebar + topbar for the "app" screens (Dashboard, Quiz Builder,
   Question Bank, Results). Ported from shell.jsx, with nav wired to the router. */
import type { ReactNode } from "react";
import { Logo } from "./motifs";
import { navigate } from "../router";
import { useAuth } from "../auth";

function initialsOf(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || "??";
}

export function Ico({ name, size = 19 }: { name: string; size?: number }) {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "home":
      return (
        <svg {...p}>
          <path d="M4 11 12 4l8 7" />
          <path d="M6 10v9h12v-9" />
        </svg>
      );
    case "build":
      return (
        <svg {...p}>
          <path d="M4 6h16M4 12h16M4 18h9" />
        </svg>
      );
    case "bank":
      return (
        <svg {...p}>
          <rect x="4" y="4" width="7" height="7" rx="2" />
          <rect x="13" y="4" width="7" height="7" rx="2" />
          <rect x="4" y="13" width="7" height="7" rx="2" />
          <rect x="13" y="13" width="7" height="7" rx="2" />
        </svg>
      );
    case "image":
      return (
        <svg {...p}>
          <rect x="3" y="5" width="18" height="14" rx="2.5" />
          <circle cx="8.5" cy="10" r="1.6" />
          <path d="m4 17 5-4.5 4 3.2 3-2.7 4 3.5" />
        </svg>
      );
    case "play":
      return (
        <svg {...p}>
          <path d="M7 5l12 7-12 7Z" fill="currentColor" stroke="none" />
        </svg>
      );
    case "chart":
      return (
        <svg {...p}>
          <path d="M5 20V10M12 20V5M19 20v-7" />
        </svg>
      );
    case "team":
      return (
        <svg {...p}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
          <path d="M16 5.5a3 3 0 0 1 0 5.6" />
          <path d="M17.5 19a5.2 5.2 0 0 0-2.3-4.3" />
        </svg>
      );
    case "user":
      return (
        <svg {...p}>
          <circle cx="12" cy="8" r="3.6" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      );
    case "search":
      return (
        <svg {...p}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.2-3.2" />
        </svg>
      );
    case "bell":
      return (
        <svg {...p}>
          <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
      );
    case "plus":
      return (
        <svg {...p}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "clock":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.5V12l3 2" />
        </svg>
      );
    case "star":
      return (
        <svg {...p}>
          <path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 17l-5.3 2.6 1-5.8L3.5 9.7l5.9-.9Z" />
        </svg>
      );
    case "trophy":
      return (
        <svg {...p}>
          <path d="M7 4h10v4a5 5 0 0 1-10 0Z" />
          <path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3M9 19h6M12 13v6" />
        </svg>
      );
    case "fire":
      return (
        <svg {...p}>
          <path d="M12 3c1 3 5 4 5 9a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 2 2 2 .5-3-1-5 1-7Z" />
        </svg>
      );
    case "check":
      return (
        <svg {...p}>
          <path d="m5 12 4.5 4.5L19 7" />
        </svg>
      );
    case "export":
      return (
        <svg {...p}>
          <path d="M12 15V4M8 8l4-4 4 4" />
          <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
        </svg>
      );
    case "filter":
      return (
        <svg {...p}>
          <path d="M4 5h16l-6 7v6l-4 2v-8Z" />
        </svg>
      );
    case "trash":
      return (
        <svg {...p}>
          <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          <path d="M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12" />
          <path d="M10 11v6M14 11v6" />
        </svg>
      );
    case "layers":
      return (
        <svg {...p}>
          <path d="M12 4 4 8l8 4 8-4-8-4Z" />
          <path d="M4 12l8 4 8-4" />
          <path d="M4 16l8 4 8-4" />
        </svg>
      );
    case "menu":
      return (
        <svg {...p}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case "close":
      return (
        <svg {...p}>
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      );
    default:
      return null;
  }
}

const NAV: [string, string, string][] = [
  ["home", "Dashboard", "/"],
  ["build", "Quizzes", "/quizzes"],
  ["bank", "Question Bank", "/bank"],
  ["chart", "Results", "/results"],
  ["user", "Account", "/account"],
];

export function Sidebar({ active }: { active: string }) {
  const { user, logout } = useAuth();
  const name = user?.displayName ?? "Baker";
  const sub = user ? (user.role === "admin" ? "Admin" : "Host") : "";
  return (
    <aside
      style={{
        width: 236,
        flex: "0 0 236px",
        alignSelf: "stretch",
        background: "var(--surface)",
        borderRight: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        padding: "26px 18px",
        gap: 26,
        position: "relative",
        zIndex: 2,
      }}
    >
      <div style={{ padding: "0 8px" }} className="bk-link" onClick={() => navigate("/")}>
        <Logo />
      </div>
      <nav style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {NAV.map(([ic, label, to]) => {
          const on = active === label;
          return (
            <div
              key={label}
              className="bk-link"
              onClick={() => navigate(to)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 13,
                padding: "11px 14px",
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 14.5,
                color: on ? "var(--red)" : "var(--ink-soft)",
                background: on ? "var(--red-tint)" : "transparent",
                boxShadow: on ? "inset 0 0 0 1px #f0cfc8" : "none",
              }}
            >
              <Ico name={ic} /> {label}
            </div>
          );
        })}
      </nav>
      <div style={{ flex: 1 }} />
      {user?.role === "admin" && (
        <div style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 14, marginTop: 2 }}>
          <div
            className="bk-link"
            onClick={() => navigate("/team")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 13,
              padding: "11px 14px",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 14.5,
              color: active === "Team" ? "var(--red)" : "var(--ink-soft)",
              background: active === "Team" ? "var(--red-tint)" : "transparent",
              boxShadow: active === "Team" ? "inset 0 0 0 1px #f0cfc8" : "none",
            }}
          >
            <Ico name="team" /> Team
          </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: "10px 12px",
          borderRadius: 14,
          background: "var(--surface-warm)",
          border: "1px solid var(--line-soft)",
        }}
      >
        <div
          className="bk-link"
          onClick={() => navigate("/account")}
          style={{ display: "flex", alignItems: "center", gap: 11, flex: 1, minWidth: 0 }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 11,
              background: "var(--gold)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
              fontSize: 15,
              flex: "0 0 auto",
            }}
          >
            {initialsOf(name)}
          </div>
          <div style={{ lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 13.5, overflowWrap: "anywhere" }}>{name}</div>
            <div style={{ fontSize: 12, color: "var(--ink-faint)", whiteSpace: "nowrap" }}>{sub}</div>
          </div>
        </div>
        <button
          className="bk-btn bk-btn--ghost bk-btn--icon"
          title="Sign out"
          style={{ width: 32, height: 32, flex: "0 0 auto" }}
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
        >
          <Ico name="export" size={15} />
        </button>
      </div>
    </aside>
  );
}

export function TopBar({ title, sub, children }: { title: string; sub?: string; children?: ReactNode }) {
  return (
    <header
      style={{
        height: 76,
        flex: "0 0 76px",
        display: "flex",
        alignItems: "center",
        gap: 18,
        padding: "0 32px",
        borderBottom: "1px solid var(--line)",
        background: "var(--surface)",
        position: "relative",
        zIndex: 2,
      }}
    >
      <div>
        <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-.02em" }}>{title}</h1>
        {sub && <div style={{ fontSize: 13, color: "var(--ink-faint)", fontWeight: 600 }}>{sub}</div>}
      </div>
      <div style={{ flex: 1 }} />
      {children}
    </header>
  );
}

export function SearchBox({
  placeholder = "Search…",
  w = 260,
  value,
  onChange,
}: {
  placeholder?: string;
  w?: number;
  value?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: w,
        padding: "10px 14px",
        background: "var(--inset)",
        borderRadius: 999,
        color: "var(--ink-faint)",
      }}
    >
      <Ico name="search" size={17} />
      {onChange ? (
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink)",
          }}
        />
      ) : (
        <span style={{ fontSize: 14, fontWeight: 600 }}>{placeholder}</span>
      )}
    </div>
  );
}
